import { FC, SyntheticEvent, useCallback, useEffect, useState } from 'react'
import { F0Box, F0Dialog, F0Text } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'

interface ScormIframeDialogProps {
  isOpen: boolean
  onClose: () => void
  src: string | null
  title?: string
  description?: string
  error?: string | null
}

export const ScormIframeDialog: FC<ScormIframeDialogProps> = ({
  isOpen,
  onClose,
  src,
  title,
  description,
  error,
}) => {
  const [iframeLoaded, setIframeLoaded] = useState(false)

  useEffect(() => {
    setIframeLoaded(false)
  }, [src])

  const handleIframeLoad = useCallback(
    (event: SyntheticEvent<HTMLIFrameElement>) => {
      try {
        const href = event.currentTarget.contentWindow?.location.href
        // Close the dialog when the iframe has navigated away from the SCORM Cloud
        // player URL (i.e. the redirect back to this app has completed). We exclude
        // 'about:blank' (initial load in some browsers) and src itself
        // (still on the player) to avoid closing the dialog prematurely.
        if (href && href !== 'about:blank' && href !== src) {
          onClose()
        } else {
          setIframeLoaded(true)
        }
      } catch {
        // Still on SCORM Cloud domain (cross-origin), course is still running
        setIframeLoaded(true)
      }
    },
    [onClose, src]
  )

  const showSpinner = !error && (!src || !iframeLoaded)

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      position="fullscreen"
      title={title}
      description={description}
    >
      <F0Box display="flex" flexDirection="column" height="full">
        {error && (
          <F0Box display="flex" justifyContent="center" alignItems="center" height="full">
            <F0Text variant="body" content={error} color="critical" />
          </F0Box>
        )}
        {showSpinner && (
          <F0Box display="flex" alignItems="center" justifyContent="center" grow>
            <Spinner />
          </F0Box>
        )}
        {src && (
          // eslint-disable-next-line factorial/no-raw-html
          <iframe
            src={src}
            title={title}
            onLoad={handleIframeLoad}
            style={{
              flex: 1,
              width: '100%',
              border: 'none',
              display: iframeLoaded ? 'block' : 'none',
            }}
          />
        )}
      </F0Box>
    </F0Dialog>
  )
}
